'use client';

import {
  ComponentProps,
  createElement,
  MutableRefObject,
  useEffect,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  formatUserAnswer,
  resolveAnswerRendererType,
} from '@/shared-project-insights';
import { ProjectResponseMessageRenderer } from '@/shared-project-response-message-renderers';
import {
  ActivityStore,
  messageFormSchema,
  useProjectResponseModeContext,
  useRealtimeVoiceMatch,
} from '@/shared-survey';
import { Chat, WAVEFORM_PLAYER_CUSTOM_STOP_EVENT } from '@/shared-ui';
import { OptionRenderType, ProjectType, QuestionType } from '@/survey-graphql';

import {
  getAnsweredValueForAiMessage,
  isFollowUpQuestion,
  getRelatedAiMessageToUserAnswer,
  getTypeOfRenderer,
  hasSubmittableAnswer,
} from './project-response-chat.helpers';
import { enumStringEquals } from './project-response.helpers';
import { MessageFormValues } from './project-response.hooks';

export type ChatMessage = ComponentProps<typeof Chat>['messages'][number];

function getQuestionTypeForVoiceMatch(
  rendererType: ReturnType<typeof getTypeOfRenderer>
) {
  switch (rendererType) {
    case 'multipleChoice':
      return QuestionType.MultipleChoice;
    case 'rating':
      return QuestionType.Rating;
    default:
      return QuestionType.OpenEnded;
  }
}

function reorderOptions(
  options: ChatMessage['options'],
  typeOfRenderer: ChatMessage['type']
) {
  if (!options?.length) return [];

  if (typeOfRenderer !== 'multipleChoice') return options;

  const otherOptionIndex = options.findIndex((option) =>
    option?.value?.includes('other')
  );

  if (otherOptionIndex === -1 || otherOptionIndex === options.length - 1) {
    return options;
  }

  return [
    ...options.slice(0, otherOptionIndex),
    ...options.slice(otherOptionIndex + 1),
    options[otherOptionIndex],
  ];
}

export function useProjectChat({
  messages,
  readonly,
  activityStore,
  disabled,
  isInsightShare = false,
  isAiVoiceEnabled = false,
  onReportProblemSubmit,
  projectType,
  numeralLocale,
  hasQuestionSessions = false,
  renderMediaOptionsInline = false,
}: {
  messages: Array<ChatMessage>;
  readonly?: boolean;
  activityStore?: MutableRefObject<ActivityStore>;
  disabled?: boolean;
  isInsightShare?: boolean;
  isAiVoiceEnabled?: boolean;
  onReportProblemSubmit?: (
    description: string,
    context: { messageId?: string; questionId?: string }
  ) => void;
  projectType?: ProjectType;
  numeralLocale?: string | null;
  /** True for a typeless (V2-authored) project, whose response is recorded as one chat session per primary question. */
  hasQuestionSessions?: boolean;
  renderMediaOptionsInline?: boolean;
}) {
  const formValue = useForm<MessageFormValues>({
    resolver: zodResolver(messageFormSchema),
    defaultValues: { content: '' },
  });
  const content = useWatch({ control: formValue.control, name: 'content' });

  const { currentTranscript, isListening, responseMode } =
    useProjectResponseModeContext();

  useEffect(() => {
    if (content !== undefined) {
      activityStore?.current.notifyContentUpdate();
    }
  }, [activityStore, content]);

  const { setValue } = formValue;
  const handleChange = (value: string) => {
    setValue('content', value);
    window.dispatchEvent(new Event(WAVEFORM_PLAYER_CUSTOM_STOP_EVENT));
  };
  const lastAssistantMessage = messages.findLast(
    (m) => m.sender === 'assistant'
  );
  const lastAssistantRendererType = lastAssistantMessage
    ? getTypeOfRenderer(lastAssistantMessage)
    : undefined;
  const isEmpty = !hasSubmittableAnswer({
    content,
    transcript: currentTranscript,
    rendererType: lastAssistantRendererType,
  });

  const lastVoiceMatchOptions =
    lastAssistantRendererType !== 'multipleChoice' &&
    lastAssistantRendererType !== 'rating'
      ? []
      : (lastAssistantMessage?.options ?? [])
          .filter((o): o is { label: string; value: string } =>
            Boolean(o?.label && o?.value)
          )
          .map((o) => ({ label: o.label, value: o.value }));

  const activeTentativeValue = useRealtimeVoiceMatch({
    transcript: currentTranscript ?? '',
    options: lastVoiceMatchOptions,
    maxSelections: lastAssistantMessage?.maxSelections,
    questionType: lastAssistantRendererType
      ? getQuestionTypeForVoiceMatch(lastAssistantRendererType)
      : QuestionType.OpenEnded,
    enabled:
      responseMode === 'voice' &&
      isListening &&
      !readonly &&
      (lastAssistantRendererType === 'multipleChoice' ||
        lastAssistantRendererType === 'rating'),
    questionId: lastAssistantMessage?.id ?? undefined,
  });

  function renderAssistantMessage(message: ChatMessage) {
    const typeOfRenderer = getTypeOfRenderer(message);
    const options = reorderOptions(message?.options, typeOfRenderer);
    const contentUrl = message.contentUrl ?? undefined;
    const answeredValue = getAnsweredValueForAiMessage(
      message,
      messages,
      hasQuestionSessions
    );
    const isActiveQuestion = lastAssistantMessage?.id === message.id;
    const isReadonly = readonly || !isActiveQuestion;

    const tentativeValue =
      isActiveQuestion && answeredValue == null ? activeTentativeValue : '';

    // In the survey app's UsabilityTesting flows the main-area stage is the
    // only place images render — force the sidebar renderer to its text
    // variant. The widget UT panel has no stage and opts out via
    // renderMediaOptionsInline.
    const effectiveOptionRenderType =
      !renderMediaOptionsInline &&
      enumStringEquals(projectType, ProjectType.UsabilityTesting) &&
      enumStringEquals(message.optionRenderType, OptionRenderType.Media)
        ? OptionRenderType.Text
        : message.optionRenderType;

    return createElement(ProjectResponseMessageRenderer, {
      app: 'survey',
      type: typeOfRenderer,
      numeralLocale,
      text: message.text,
      optionRenderType: effectiveOptionRenderType,
      onChange: handleChange,
      value: answeredValue ?? content,
      tentativeValue: tentativeValue || undefined,
      options,
      readonly: isReadonly,
      contentUrl,
      assets: message.assets,
      multiSelect: message.maxSelections !== null,
      id: message.id,
      avatarUrl: message?.avatarUrl,
      audio: message.audio ?? undefined,
      maxSelections: message.maxSelections ?? undefined,
      isInsightShare,
      isAiVoiceEnabled: isAiVoiceEnabled && responseMode === 'voice',
      totalResponses: message.totalResponses ?? undefined,
      shouldAnimate: message.shouldAnimate ?? false,
      isFollowUp: isFollowUpQuestion(message),
      onSubmit: onReportProblemSubmit,
      questionId: message.questionId ?? undefined,
      subQuestions: message.subQuestions,
      notApplicableLabel: message.notApplicableLabel,
    });
  }

  function formatUserMessage(message: ChatMessage) {
    if (message.transcript) {
      return message.transcript;
    }
    const relatedMessage = getRelatedAiMessageToUserAnswer(
      messages.findIndex((e) => e.id === message.id),
      messages,
      hasQuestionSessions
    );

    if (!relatedMessage) return message.text;
    const typeOfRenderer = resolveAnswerRendererType({
      questionType: relatedMessage.type,
      subQuestionCount: relatedMessage.subQuestions?.length ?? 0,
      answerText: message.text,
    });
    const options = reorderOptions(relatedMessage.options, typeOfRenderer);

    return formatUserAnswer(
      typeOfRenderer,
      message.text,
      options,
      relatedMessage.optionRenderType,
      relatedMessage.subQuestions
    );
  }

  const disableForm = disabled || isEmpty;

  return {
    renderAssistantMessage,
    lastAssistantMessage,
    formatUserMessage,
    formValue,
    content,
    currentTranscript,
    isEmpty,
    disableForm,
  };
}
